// Thin client-side helpers for the uniapp-backend JWT issued by
// POST /user/login. The JWT is signed with the BE's
// ACCESS_TOKEN_SECRET; the FE never verifies the signature (that's
// the BE's job on every protected request), but it does decode the
// payload locally to route the user past role-gated <Route> guards
// without a round-trip per page load.
//
// Claim shape (see uniapp-backend user.service generateToken):
//   { sub: '<userId>', role: [<number>...], zalo, store, center,
//     source, elearningUserId }
// Note: tokens are issued without `exp` today, so we don't enforce
// expiry here; once BE adds exp this helper will pick it up
// automatically.

const TOKEN_KEY = 'user-jwt-tk';
const REFRESH_KEY = 'user-jwt-rftk';
const ROLE_KEY = 'user-role';

// uniapp-backend roles constant — keep numeric values in sync with
// apps/uniapp-backend/src/constants/roles.js. We mirror the few
// driving-dat actually checks; full list isn't needed here.
export const ROLES = {
  ADMIN: 1,
  SUPER_ADMIN: 99,
  DRIVING_ADMIN: 2,
  DRIVING_TEACHER: 12,
  DRIVING_ADMIN_NEW: 10,
  DRIVING_REPORTER: 13,
  DRIVING_DAT_ADMIN: 15,
  // dat-only — see roles.constant.js DRIVING.DAT_TEACHER.
  DRIVING_DAT_TEACHER: 16,
};

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

export function setAuth({ token, refreshToken, role }) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    if (role != null) {
      // Persist as JSON so both arrays and scalars survive the round-trip.
      localStorage.setItem(ROLE_KEY, JSON.stringify(Array.isArray(role) ? role : [role]));
    }
  } catch (_) { /* SSR-safe */ }
}

export function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ROLE_KEY);
  } catch (_) { /* noop */ }
}

// Base64-url decode without external dep. Pure browser API.
function b64UrlDecode(input) {
  const pad = '='.repeat((4 - (input.length % 4)) % 4);
  const b64 = (input + pad).replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(
    atob(b64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
}

export function decodeJwt(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(b64UrlDecode(parts[1]));
  } catch (_) {
    return null;
  }
}

// Pull the role array straight from the JWT payload — the BE's source
// of truth — so a localStorage role tampered by hand still won't open
// guarded routes (the JWT signature would have to be forged too).
export function getRoleArray() {
  const claims = decodeJwt(getToken());
  if (!claims) return [];
  const role = claims.role;
  if (!role) return [];
  return Array.isArray(role) ? role : [role];
}

export function hasAnyRole(...allowed) {
  const flat = allowed.flat().filter((r) => r != null);
  if (flat.length === 0) return false;
  const got = getRoleArray();
  return got.some((r) => flat.includes(Number(r)) || flat.includes(r));
}

export function isAuthenticated() {
  return !!getToken() && getRoleArray().length > 0;
}

export function isTeacherOrAdmin() {
  return hasAnyRole(
    ROLES.ADMIN,
    ROLES.SUPER_ADMIN,
    ROLES.DRIVING_ADMIN,
    ROLES.DRIVING_ADMIN_NEW,
    ROLES.DRIVING_TEACHER,
    ROLES.DRIVING_REPORTER,
  );
}

export function isDrivingAdmin() {
  return hasAnyRole(
    ROLES.ADMIN,
    ROLES.SUPER_ADMIN,
    ROLES.DRIVING_ADMIN,
    ROLES.DRIVING_ADMIN_NEW,
  );
}
